import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
import { Plus, Search, Edit, Trash2, Calculator, FileDown, FileUp, Download, ChevronRight, AlertCircle, Check, X, Settings, Tag, BarChart3, List, Upload, CheckCircle, XCircle, RefreshCw, Copy, FileText } from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
import ProductFamilyTabs, { PRODUCT_FAMILIES, getProductFamilyMeta } from './ProductFamilyTabs';
import DuplicateToFamilyDialog from './DuplicateToFamilyDialog';
import FamilyScopePicker, { FamilyBadge, CrossFamilyWarning } from './FamilyScopePicker';
import SkuDependencyDialog from './SkuDependencyDialog';
import { toast } from 'sonner';
const API_URL = API_BASE;

/**
 * Safe fetch helper that reads the response body exactly once.
 * Eliminates all "body already used" errors.
 */
async function safeFetch(url, options = {}) {
  const res = await fetch(url, options);
  
  // Read body exactly once as text
  const raw = await res.text();
  
  // Try to parse as JSON
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    // Non-JSON response, keep raw as fallback
  }
  
  if (!res.ok) {
    // Extract error message from various formats
    let errorMessage = 'Request failed';
    if (data) {
      if (typeof data.detail === 'string') {
        errorMessage = data.detail;
      } else if (Array.isArray(data.detail)) {
        // Pydantic validation errors
        errorMessage = data.detail.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ');
      } else if (data.message) {
        errorMessage = data.message;
      } else if (data.detail) {
        errorMessage = JSON.stringify(data.detail);
      }
    } else if (raw) {
      errorMessage = raw;
    }
    throw new Error(errorMessage);
  }
  
  return data;
}

export default function SalesRateCardTab() {
  const [rateCards, setRateCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Multi-product family scoping (Phase 2)
  const [productFamily, setProductFamily] = useState('vinyl');
  const [familyCounts, setFamilyCounts] = useState({ vinyl: 0, cd: 0, dvd: 0, bluray: 0 });
  // Phase 2 (soft scoping): scope picker for the BOM Product dropdown
  // inside the Rate Item modal. Default = current family.
  const [bomScope, setBomScope] = useState('current');
  
  // Secondary tab for selected card: 'items' or 'margin'
  const [cardDetailTab, setCardDetailTab] = useState('items');
  
  // Modals
  const [showCardModal, setShowCardModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateSourceCard, setDuplicateSourceCard] = useState(null);
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Standalone "Linked artwork templates" dialog — opened by clicking the
  // count badge in the Templates column so admins can inspect/edit the
  // template link without loading the full item-edit modal.
  const [templatesDialogItem, setTemplatesDialogItem] = useState(null);

  // SKU-in-use dependency dialog. When the admin hits Delete on a Sales
  // SKU that's still referenced anywhere (bundles / orders / customer
  // overrides / cross-card add-ons), we open this dialog with the
  // reference breakdown from the backend so they can Cancel / Replace /
  // Force Delete. See SkuDependencyDialog for the interaction spec.
  const [depDialog, setDepDialog] = useState({
      open: false,
      item: null,           // { id, sales_sku, display_name, rate_card_id }
      dependencies: null,   // GET /items/{id}/dependencies response
      allItems: [],         // pool for the "Replace SKU" dropdown
  });
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importCommitting, setImportCommitting] = useState(false);
  const [importMode, setImportMode] = useState('update_and_add');
  const [deleteMissing, setDeleteMissing] = useState(false);
  const fileInputRef = useRef(null);
  
  // BOMs and Categories for dropdowns
  const [boms, setBoms] = useState([]);
  const [categories, setCategories] = useState([]);

  const getToken = () => localStorage.getItem('token');

  const fetchRateCards = useCallback(async () => {
    try {
      setLoading(true);
      const data = await safeFetch(`${API_URL}/api/sales/rate-cards?product_family=${encodeURIComponent(productFamily)}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      setRateCards(data || []);
      
      // Auto-select active card in current family
      const activeCard = (data || []).find(c => c.status === 'active');
      if (activeCard) {
        setSelectedCard(activeCard);
      } else if ((data || []).length === 0) {
        setSelectedCard(null);
        setItems([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [productFamily]);

  // Fetch counts for all families (for the tab badges)
  const fetchFamilyCounts = useCallback(async () => {
    const families = ['vinyl', 'cd', 'dvd', 'bluray'];
    try {
      const results = await Promise.all(
        families.map(fam =>
          safeFetch(`${API_URL}/api/sales/rate-cards?product_family=${fam}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
          }).then(d => [fam, (d || []).length]).catch(() => [fam, 0])
        )
      );
      setFamilyCounts(Object.fromEntries(results));
    } catch (err) {
      console.error('Failed to fetch family counts:', err);
    }
  }, []);

  const fetchItems = useCallback(async (cardId) => {
    if (!cardId) return;
    try {
      setItemsLoading(true);
      const data = await safeFetch(`${API_URL}/api/sales/rate-cards/${cardId}/items`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      setItems(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  // Per-BOM quote-gate cache: { [bom_product_id]: { quote_required, count, components } }
  // Populated by fan-out calls to POST /api/bom/quote-check whenever the
  // items list changes. Reuses the exact same backend endpoint the BOM
  // editor and the pricing engine use, so the Sales Rate Items list and
  // the editor badge can never disagree with the live quote gate.
  const [bomQuoteGateByProductId, setBomQuoteGateByProductId] = useState({});

  useEffect(() => {
    const uniqueBomIds = Array.from(
      new Set(
        (items || [])
          .map((it) => (it.generate_purchase_line !== false ? it.bom_product_id : null))
          .filter(Boolean)
      )
    );
    if (uniqueBomIds.length === 0) {
      setBomQuoteGateByProductId({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          uniqueBomIds.map(async (pid) => {
            try {
              const res = await fetch(`${API_URL}/api/bom/quote-check`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ product_id: pid }),
              });
              if (!res.ok) return [pid, { quote_required: false, count: 0, components: [] }];
              const body = await res.json();
              return [pid, {
                quote_required: !!body.quote_required,
                count: body.count || 0,
                components: body.components || [],
              }];
            } catch {
              return [pid, { quote_required: false, count: 0, components: [] }];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        for (const [pid, gate] of results) map[pid] = gate;
        setBomQuoteGateByProductId(map);
      } catch {
        if (!cancelled) setBomQuoteGateByProductId({});
      }
    })();
    return () => { cancelled = true; };
  }, [items]);

  // Cross-card SKU → artwork_template_ids map, used to surface INHERITED
  // artwork templates in the Templates column. When a rate item lists
  // setup_fee_skus or per_unit_addon_skus that point at OTHER items,
  // any templates linked to those linked items should also appear as
  // available on the parent row. Kept as a plain object (sku string →
  // string[] of template ids) for cheap O(1) lookups in the row render.
  const [crossCardTemplateMap, setCrossCardTemplateMap] = useState({});

  const fetchCrossCardTemplateMap = useCallback(async () => {
    try {
      const res = await safeFetch(`${API_URL}/api/sales/rate-items/cross-card`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const list = Array.isArray(res) ? res : (res?.items || []);
      const map = {};
      for (const it of list) {
        if (it?.sales_sku && Array.isArray(it.artwork_template_ids) && it.artwork_template_ids.length > 0) {
          map[it.sales_sku] = it.artwork_template_ids;
        }
      }
      setCrossCardTemplateMap(map);
    } catch (err) {
      // Non-fatal: without the map, the Templates column falls back to
      // showing direct-only counts, which is what it did before.
      console.error('Failed to fetch cross-card SKU template map:', err);
    }
  }, []);

  const fetchBoms = useCallback(async () => {
    try {
      // Phase 2 (soft scoping): if scope='current' use the active family,
      // otherwise the explicit scope value (incl. 'all' for no filter).
      const resolvedFamily = bomScope === 'current' ? productFamily : bomScope;
      const data = await safeFetch(
        `${API_URL}/api/bom/products?product_family=${encodeURIComponent(resolvedFamily)}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      setBoms(data || []);
    } catch (err) {
      console.error('Failed to fetch BOMs:', err);
    }
  }, [productFamily, bomScope]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await safeFetch(`${API_URL}/api/sales/categories?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchRateCards();
    fetchFamilyCounts();
    fetchBoms();
    fetchCategories();
    fetchCrossCardTemplateMap();
  }, [fetchRateCards, fetchFamilyCounts, fetchBoms, fetchCategories, fetchCrossCardTemplateMap]);

  useEffect(() => {
    if (selectedCard) {
      fetchItems(selectedCard.id);
      // Refresh the cross-card template map too — the admin may have
      // added/removed template links on another rate card between
      // navigating cards, and we don't want a stale badge count here.
      fetchCrossCardTemplateMap();
    }
  }, [selectedCard, fetchItems, fetchCrossCardTemplateMap]);

  // Rate Card CRUD
  const handleSaveCard = async (cardData) => {
    try {
      const method = editingCard ? 'PATCH' : 'POST';
      const url = editingCard 
        ? `${API_URL}/api/sales/rate-cards/${editingCard.id}`
        : `${API_URL}/api/sales/rate-cards`;
      
      // For new cards, attach the currently-active product_family
      const payload = editingCard ? cardData : { ...cardData, product_family: productFamily };
      
      await safeFetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      setShowCardModal(false);
      setEditingCard(null);
      fetchRateCards();
      fetchFamilyCounts();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDuplicateToFamily = async (payload) => {
    if (!duplicateSourceCard) return;
    setDuplicateBusy(true);
    try {
      const result = await safeFetch(
        `${API_URL}/api/sales/rate-cards/${duplicateSourceCard.id}/duplicate-to-family`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      setShowDuplicateModal(false);
      setDuplicateSourceCard(null);
      // Jump to the target family + refresh
      setProductFamily(payload.product_family);
      fetchFamilyCounts();
      // Show a brief summary (warnings if any)
      const msg = [
        `New ${payload.product_family.toUpperCase()} card: "${result.new_card_name}"`,
        `Items copied: ${result.items_copied}`,
        result.items_needing_bom_review > 0
          ? `${result.items_needing_bom_review} item(s) need BOM review`
          : null,
      ].filter(Boolean).join('\n');
      alert(msg);
    } catch (err) {
      alert(`Duplicate failed: ${err.message}`);
    } finally {
      setDuplicateBusy(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Delete this rate card? All items will be removed.')) return;
    
    try {
      await safeFetch(`${API_URL}/api/sales/rate-cards/${cardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (selectedCard?.id === cardId) {
        setSelectedCard(null);
        setItems([]);
      }
      fetchRateCards();
      fetchFamilyCounts();
    } catch (err) {
      alert(err.message);
    }
  };

  // Rate Item CRUD
  const [savingItemIds, setSavingItemIds] = useState(new Set());

  const handleToggleItemActive = async (item) => {
    if (savingItemIds.has(item.id)) return;   // prevent double-clicks
    const nextActive = !item.is_active;
    // Optimistic update so the icon flips immediately.
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_active: nextActive } : i)));
    setSavingItemIds((prev) => new Set(prev).add(item.id));
    try {
      await safeFetch(`${API_URL}/api/sales/items/${item.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: nextActive }),
      });
      toast.success(nextActive ? 'Item activated' : 'Item deactivated');
    } catch (err) {
      // Roll back optimistic flip on failure.
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_active: item.is_active } : i)));
      toast.error(err.message || 'Failed to update status');
    } finally {
      setSavingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      const method = editingItem ? 'PATCH' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/sales/items/${editingItem.id}`
        : `${API_URL}/api/sales/items`;
      
      const payload = editingItem ? itemData : { ...itemData, rate_card_id: selectedCard.id };
      
      await safeFetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      setShowItemModal(false);
      setEditingItem(null);
      fetchItems(selectedCard.id);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    // Dependency-aware delete. Preflight the /dependencies endpoint —
    // when nothing points at this SKU we keep the old confirm() flow
    // for zero UX friction; otherwise we surface the SkuDependencyDialog
    // with counts + Cancel/Replace/Force actions.
    try {
      const deps = await safeFetch(
        `${API_URL}/api/sales/items/${itemId}/dependencies`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } },
      );

      if ((deps?.total_references || 0) === 0) {
        if (!window.confirm('Delete this price item?')) return;
        await safeFetch(`${API_URL}/api/sales/items/${itemId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getToken()}` },
        });
        fetchItems(selectedCard.id);
        toast.success('Price item deleted');
        return;
      }

      // Fetch the pool of possible replacements (cross-card) so the
      // Replace SKU dropdown has something to show. We deliberately
      // include items from every rate card — an admin retiring a card
      // may want to remap to an item in a newer one.
      let pool = [];
      try {
        const res = await safeFetch(
          `${API_URL}/api/sales/rate-items/cross-card`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } },
        );
        const list = Array.isArray(res) ? res : (res?.items || []);
        pool = list
          .filter((r) => r.id !== itemId)
          .map((r) => ({
            id: r.id,
            sales_sku: r.sales_sku,
            display_name: r.display_name,
            rate_card_id: r.rate_card_id,
          }));
      } catch {
        // Non-fatal: dialog still opens; Replace button just won't be
        // usable and the admin can Cancel or Force Delete.
      }

      const sourceItem = (items || []).find((i) => i.id === itemId) || { id: itemId };
      setDepDialog({
        open: true,
        item: {
          id: itemId,
          sales_sku: sourceItem.sales_sku || deps.sales_sku,
          display_name: sourceItem.display_name,
          rate_card_id: sourceItem.rate_card_id,
        },
        dependencies: deps,
        allItems: pool,
      });
    } catch (err) {
      alert(err.message);
    }
  };

  // Close the dependency dialog (Cancel + successful actions).
  const closeDepDialog = () => setDepDialog({ open: false, item: null, dependencies: null, allItems: [] });

  // Called from SkuDependencyDialog when the admin picks a replacement
  // and confirms. Backend performs the swap across every reference and
  // deletes the source item in one go, then we refresh the list.
  const handleReplaceSku = async (replacementItemId) => {
    if (!depDialog.item?.id) return;
    try {
      await safeFetch(
        `${API_URL}/api/sales/items/${depDialog.item.id}/replace-with`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ replacement_item_id: replacementItemId }),
        },
      );
      toast.success('SKU replaced and deleted');
      closeDepDialog();
      fetchItems(selectedCard.id);
    } catch (err) {
      // Bubble up so the dialog can render the error inline instead of
      // toasting away where the admin can't see it.
      throw err;
    }
  };

  // Force delete — backend still refuses when active orders reference
  // the SKU (409). We surface the error inline in the dialog.
  const handleForceDeleteSku = async () => {
    if (!depDialog.item?.id) return;
    try {
      await safeFetch(
        `${API_URL}/api/sales/items/${depDialog.item.id}?force=true`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getToken()}` },
        },
      );
      toast.success('SKU force-deleted; references cleaned up');
      closeDepDialog();
      fetchItems(selectedCard.id);
    } catch (err) {
      throw err;
    }
  };

  const handleCloneItem = async (itemId) => {
    // Deep-clone on the server, then open the copy in Edit mode so the
    // admin can immediately tweak the SKU/price and save. No confirm
    // prompt — clone is non-destructive.
    try {
      const cloned = await safeFetch(`${API_URL}/api/sales/items/${itemId}/clone`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      await fetchItems(selectedCard.id);
      setEditingItem(cloned);
      setShowItemModal(true);
    } catch (err) {
      alert(err.message || 'Failed to clone item');
    }
  };

  const handleExport = async () => {
    if (!selectedCard) return;
    try {
      const res = await fetch(`${API_URL}/api/sales/rate-cards/${selectedCard.id}/export`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to export rate card');
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_rate_card_${selectedCard.name.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    }
  };

  // Import handlers
  const handleDownloadTemplate = async (format = 'csv') => {
    if (!selectedCard) return;
    try {
      const res = await fetch(`${API_URL}/api/sales/rate-cards/${selectedCard.id}/import/template?format=${format}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to download template');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `price_items_template_${selectedCard.id}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportPreview(null);
    }
  };

  const handlePreviewImport = async () => {
    if (!selectedCard || !importFile) return;
    
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const params = new URLSearchParams({
        import_mode: importMode,
        delete_missing: deleteMissing.toString()
      });
      
      const data = await safeFetch(`${API_URL}/api/sales/rate-cards/${selectedCard.id}/import/preview?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      
      setImportPreview(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleCommitImport = async () => {
    if (!selectedCard || !importPreview?.parsed_items) return;
    
    setImportCommitting(true);
    try {
      const result = await safeFetch(`${API_URL}/api/sales/rate-cards/${selectedCard.id}/import/commit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          parsed_items: importPreview.parsed_items,
          items_to_delete: importPreview.items_to_delete || []
        })
      });
      
      const parts = [];
      if (result.created > 0) parts.push(`${result.created} created`);
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      if (result.deleted > 0) parts.push(`${result.deleted} deleted`);
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
      alert(`Import complete: ${parts.join(', ')}`);
      
      // Reset and close modal
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview(null);
      setImportMode('update_and_add');
      setDeleteMissing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh items
      fetchItems(selectedCard.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setImportCommitting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportMode('update_and_add');
    setDeleteMissing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredItems = items.filter(item =>
    item.sales_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getMarginColor = (pct) => {
    if (pct >= 50) return 'text-green-600 bg-green-50';
    if (pct >= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    active: 'bg-green-100 text-green-700',
    archived: 'bg-orange-100 text-orange-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Product Family Tabs */}
      <div className="flex items-center justify-between">
        <ProductFamilyTabs
          value={productFamily}
          onChange={(fam) => {
            setProductFamily(fam);
            setSelectedCard(null);
            setItems([]);
          }}
          counts={familyCounts}
        />
        <span className="text-xs text-slate-500">
          Showing <span className="font-medium text-slate-700">{getProductFamilyMeta(productFamily).label}</span> rate cards
        </span>
      </div>

      {/* Rate Cards List */}
      <div className="bg-white rounded-lg border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-medium text-gray-900">
            Sales Rate Cards
            <span className="ml-2 text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full uppercase">
              {getProductFamilyMeta(productFamily).label}
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              <Tag className="w-4 h-4" />
              Categories ({categories.filter(c => c.is_active).length})
            </button>
            <button
              onClick={() => { setEditingCard(null); setShowCardModal(true); }}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              data-testid="new-rate-card-btn"
            >
              <Plus className="w-4 h-4" />
              New Rate Card
            </button>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {rateCards.length === 0 ? (
            <EmptyFamilyState
              family={productFamily}
              onCreateNew={() => { setEditingCard(null); setShowCardModal(true); }}
              onPickVinylToDuplicate={async () => {
                // Fetch vinyl cards and present a quick picker
                try {
                  const vinylCards = await safeFetch(
                    `${API_URL}/api/sales/rate-cards?product_family=vinyl`,
                    { headers: { 'Authorization': `Bearer ${getToken()}` } }
                  );
                  if (!vinylCards || vinylCards.length === 0) {
                    alert('No Vinyl rate cards exist to duplicate from.');
                    return;
                  }
                  const active = vinylCards.find(c => c.status === 'active') || vinylCards[0];
                  setDuplicateSourceCard(active);
                  setShowDuplicateModal(true);
                } catch (err) {
                  alert(`Could not load Vinyl rate cards: ${err.message}`);
                }
              }}
            />
          ) : (
            rateCards.map(card => (
              <div
                key={card.id}
                onClick={() => setSelectedCard(card)}
                className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${
                  selectedCard?.id === card.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                }`}
                data-testid={`rate-card-row-${card.id}`}
              >
                <div className="flex items-center gap-3">
                  <div>
                    <div className="font-medium text-gray-900">{card.name}</div>
                    <div className="text-sm text-gray-500">
                      {card.item_count} items • {card.currency}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[card.status]}`}>
                    {card.status}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDuplicateSourceCard(card); setShowDuplicateModal(true); }}
                    className="p-1 hover:bg-indigo-50 rounded text-indigo-600"
                    title="Duplicate to family"
                    data-testid={`duplicate-rate-card-${card.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingCard(card); setShowCardModal(true); }}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Edit className="w-4 h-4 text-gray-500" />
                  </button>
                  {card.status !== 'active' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.id); }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Selected Card Details */}
      {selectedCard && (
        <div className="bg-white rounded-lg border border-border">
          {/* Card Header with Secondary Tabs */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">{selectedCard.name}</h3>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[selectedCard.status]}`}>
                {selectedCard.status}
              </span>
            </div>
            {/* Secondary Tab Navigation */}
            <div className="flex gap-1 border-b border-border -mb-3">
              <button
                onClick={() => setCardDetailTab('items')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cardDetailTab === 'items'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-price-items"
              >
                <List className="w-4 h-4" />
                Price Items
              </button>
              <button
                onClick={() => setCardDetailTab('margin')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cardDetailTab === 'margin'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-margin-analysis"
              >
                <BarChart3 className="w-4 h-4" />
                Margin Analysis
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {cardDetailTab === 'items' && (
            <PriceItemsView
              items={items}
              filteredItems={filteredItems}
              itemsLoading={itemsLoading}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleExport={handleExport}
              setEditingItem={setEditingItem}
              setShowItemModal={setShowItemModal}
              handleDeleteItem={handleDeleteItem}
              handleCloneItem={handleCloneItem}
              handleToggleItemActive={handleToggleItemActive}
              savingItemIds={savingItemIds}
              onImportClick={() => setShowImportModal(true)}
              onDownloadTemplate={() => handleDownloadTemplate('csv')}
              onOpenTemplatesDialog={setTemplatesDialogItem}
              crossCardTemplateMap={crossCardTemplateMap}
              bomQuoteGateByProductId={bomQuoteGateByProductId}
            />
          )}

          {cardDetailTab === 'margin' && (
            <MarginAnalysisView
              selectedCard={selectedCard}
              items={items}
              getMarginColor={getMarginColor}
            />
          )}
        </div>
      )}

      {/* Rate Card Modal */}
      {showCardModal && (
        <RateCardModal
          card={editingCard}
          onSave={handleSaveCard}
          onClose={() => { setShowCardModal(false); setEditingCard(null); }}
        />
      )}

      {/* Rate Item Modal */}
      {showItemModal && selectedCard && (
        <RateItemModal
          item={editingItem}
          boms={boms}
          categories={categories}
          allItems={items}
          selectedCardId={selectedCard.id}
          selectedCardName={selectedCard.name}
          productFamily={selectedCard.product_family || productFamily}
          activeFamily={productFamily}
          bomScope={bomScope}
          onBomScopeChange={setBomScope}
          onSave={handleSaveItem}
          onClose={() => { setShowItemModal(false); setEditingItem(null); }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && selectedCard && (
        <ImportModal
          selectedCard={selectedCard}
          importFile={importFile}
          importPreview={importPreview}
          importLoading={importLoading}
          importCommitting={importCommitting}
          fileInputRef={fileInputRef}
          onFileSelect={handleFileSelect}
          onPreview={handlePreviewImport}
          onCommit={handleCommitImport}
          onReset={resetImport}
          onDownloadTemplate={handleDownloadTemplate}
          onClose={() => {
            setShowImportModal(false);
            resetImport();
          }}
          importMode={importMode}
          setImportMode={setImportMode}
          deleteMissing={deleteMissing}
          setDeleteMissing={setDeleteMissing}
        />
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onRefresh={fetchCategories}
        />
      )}

      {/* Duplicate to Family Modal */}
      <DuplicateToFamilyDialog
        open={showDuplicateModal}
        onClose={() => { setShowDuplicateModal(false); setDuplicateSourceCard(null); }}
        sourceLabel={duplicateSourceCard?.name || ''}
        sourceFamily={duplicateSourceCard?.product_family || 'vinyl'}
        entityType="rate_card"
        onConfirm={handleDuplicateToFamily}
        busy={duplicateBusy}
      />

      {/* Standalone Linked-Templates Dialog — opened from the Templates
          count badge in the item table. Reuses the same picker used by
          the full item-edit modal so the UX is consistent. */}
      {templatesDialogItem && (
        <LinkedTemplatesDialog
          item={templatesDialogItem}
          onClose={() => setTemplatesDialogItem(null)}
          onSaved={(nextIds) => {
            // Optimistic in-place update so the count badge refreshes
            // immediately without refetching the entire item list.
            setItems(prev => prev.map(i =>
              i.id === templatesDialogItem.id
                ? { ...i, artwork_template_ids: nextIds }
                : i
            ));
          }}
        />
      )}

      {/* SKU dependency dialog — see handleDeleteItem for the flow. */}
      <SkuDependencyDialog
        open={depDialog.open}
        onClose={closeDepDialog}
        sourceItem={depDialog.item}
        dependencies={depDialog.dependencies}
        availableItems={depDialog.allItems}
        onReplace={handleReplaceSku}
        onForceDelete={handleForceDeleteSku}
      />
    </div>
  );
}

// Empty-state for an unpopulated family (Phase 2 UX)
function EmptyFamilyState({ family, onCreateNew, onPickVinylToDuplicate }) {
  const meta = getProductFamilyMeta(family);
  const Icon = meta.icon;
  const isVinyl = family === 'vinyl';
  return (
    <div className="px-6 py-12 text-center" data-testid={`empty-family-${family}`}>
      <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mb-3">
        <Icon className="w-6 h-6 text-slate-500" />
      </div>
      <p className="text-sm font-medium text-slate-900">
        No {meta.label} rate cards yet
      </p>
      <p className="text-xs text-slate-500 mt-1">
        {isVinyl
          ? 'Create your first rate card to start pricing products.'
          : `Start a ${meta.label} pricing setup — either fresh or based on your Vinyl template.`}
      </p>
      <div className="mt-4 flex items-center justify-center gap-2">
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
          data-testid={`empty-create-${family}`}
        >
          <Plus className="w-4 h-4" />
          Create your first {meta.label} rate card
        </button>
        {!isVinyl && (
          <button
            onClick={onPickVinylToDuplicate}
            className="px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2"
            data-testid={`empty-duplicate-vinyl-${family}`}
          >
            <Copy className="w-4 h-4" />
            Duplicate from Vinyl
          </button>
        )}
      </div>
    </div>
  );
}

// Rate Card Modal Component
function RateCardModal({ card, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: card?.name || '',
    currency: card?.currency || 'EUR',
    status: card?.status || 'draft',
    effective_from: card?.effective_from || '',
    effective_to: card?.effective_to || '',
    notes_internal: card?.notes_internal || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-medium text-gray-900">
            {card ? 'Edit Rate Card' : 'New Rate Card'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="DKK">DKK</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
              <input
                type="date"
                value={formData.effective_from}
                onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective To</label>
              <input
                type="date"
                value={formData.effective_to}
                onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
            <textarea
              value={formData.notes_internal}
              onChange={(e) => setFormData({ ...formData, notes_internal: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Rate Item Modal Component
function RateItemModal({
  item,
  boms,
  categories,
  allItems,
  selectedCardId,
  selectedCardName,
  productFamily = 'vinyl',
  activeFamily = 'vinyl',
  bomScope = 'current',
  onBomScopeChange = null,
  onSave,
  onClose,
}) {
  const activeCategories = categories?.filter(c => c.is_active) || [];
  
  // Helper to get auth token
  const getToken = () => localStorage.getItem('token');
  
  // Cross-card items for setup fee / add-on selection
  const [crossCardItems, setCrossCardItems] = useState([]);
  const [crossCardLoading, setCrossCardLoading] = useState(false);
  const [setupFeeSource, setSetupFeeSource] = useState('all'); // 'current' | 'all'
  const [addonSource, setAddonSource] = useState('all');
  // Phase 2 (soft scoping): independent family scope for the setup-fee and
  // add-on source-rate-card pools.
  //
  // Default heuristic: if the item ALREADY has linked entries whose
  // `source_rate_card_id` points to a DIFFERENT card than the item itself,
  // start with the 'all' scope so the linked SKU stays visible in the
  // browse pool on reopen. Otherwise fall back to 'current'. This mirrors
  // the actual state the user left the modal in — otherwise the filter
  // silently reverts to Blu-ray/Vinyl/etc. every time the item is opened.
  const _hasCrossCardLinks = (entries) => {
    if (!Array.isArray(entries) || !item?.rate_card_id) return false;
    return entries.some(
      e => e && e.source_rate_card_id && e.source_rate_card_id !== item.rate_card_id,
    );
  };
  const [setupFeeFamilyScope, setSetupFeeFamilyScope] = useState(
    _hasCrossCardLinks(item?.setup_fee_skus) ? 'all' : 'current',
  );
  const [addonFamilyScope, setAddonFamilyScope] = useState(
    _hasCrossCardLinks(item?.per_unit_addon_skus) ? 'all' : 'current',
  );

  // Live BOM quote-gate preview for the currently selected (possibly
  // UNSAVED) BOM dropdown value. Debounced POST to /api/bom/quote-check
  // so the admin sees the yellow "Supplier Quote Required (from BOM)"
  // badge update immediately when they pick a different BOM.
  //
  // We never mutate ``formData.requires_quote`` from this effect —
  // that flag stays an independent manual trigger per the product
  // requirement. The badge below the BOM selector + the read-only
  // Effective Quote Status row are the sole UI consumers.
  const [bomQuoteGate, setBomQuoteGate] = useState({
    quote_required: false,
    count: 0,
    components: [],
    loading: false,
  });
  
  useEffect(() => {
    const fetchCrossCard = async () => {
      setCrossCardLoading(true);
      try {
        // Phase 2 (soft scoping): pick the broader of setup-fee and add-on
        // family scopes. If either is 'all' we fetch 'all'; otherwise we
        // resolve 'current' to the active family.
        const resolve = (scope) => (scope === 'current' ? activeFamily : scope);
        const setupFam = resolve(setupFeeFamilyScope);
        const addonFam = resolve(addonFamilyScope);
        let finalFamily;
        if (setupFam === 'all' || addonFam === 'all') {
          finalFamily = 'all';
        } else if (setupFam === addonFam) {
          finalFamily = setupFam;
        } else {
          // Two different specific families chosen → fetch 'all' and let
          // the in-modal client-side filter narrow back down per pool.
          finalFamily = 'all';
        }
        const url = `${API_URL}/api/sales/rate-items/cross-card?product_family=${encodeURIComponent(finalFamily)}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCrossCardItems(data || []);
        }
      } catch (err) {
        console.error('Failed to fetch cross-card items:', err);
      } finally {
        setCrossCardLoading(false);
      }
    };
    fetchCrossCard();
  }, [activeFamily, setupFeeFamilyScope, addonFamilyScope]);
  
  // Get unique source rate cards from cross-card items (carries family info)
  const sourceRateCards = React.useMemo(() => {
    const map = new Map();
    crossCardItems.forEach(i => {
      if (i.source_rate_card_id && !map.has(i.source_rate_card_id)) {
        map.set(i.source_rate_card_id, {
          name: i.source_rate_card_name,
          product_family: i.source_rate_card_product_family || 'vinyl',
        });
      }
    });
    return Array.from(map.entries()).map(([id, info]) => ({
      id,
      name: info.name,
      product_family: info.product_family,
    }));
  }, [crossCardItems]);
  
  // Get unique categories from the items pool
  const getCrossCardCategories = (source) => {
    let pool;
    if (source === 'all') {
      pool = crossCardItems;
    } else if (source === 'current') {
      pool = crossCardItems.filter(i => i.source_rate_card_id === selectedCardId);
    } else {
      pool = crossCardItems.filter(i => i.source_rate_card_id === source);
    }
    const cats = new Set();
    pool.forEach(i => { if (i.category) cats.add(i.category); });
    return Array.from(cats).sort();
  };
  
  // Parse existing setup_fee_skus into new format
  // [{sku, applies_to, source_rate_card_id, source_rate_card_name, source_item_id}]
  //
  // CRITICAL: preserve every source_* field. Stripping them here means the
  // very next save (even if the user only edited the applies_to checkboxes)
  // would persist `null` for source_rate_card_id — and the cross-family
  // link would be lost. See "Cross-family Setup Fee / Add-on links are
  // not persisted correctly" bug.
  // Small helper to synthesize a locally-unique entry id when the backend
  // hasn't yet assigned one (legacy rows). `crypto.randomUUID` is available
  // in every browser we support; fall back to a timestamped counter for
  // very old Safari embeds.
  const makeEntryId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const parseExistingSetupFees = (skus) => {
    if (!skus || !Array.isArray(skus)) return [];
    return skus.map(entry => {
      if (typeof entry === 'string') {
        // Legacy string format - default to new_order, qty 1
        return { entry_id: makeEntryId(), sku: entry, applies_to: ['new_order'], quantity: 1 };
      }
      // New object format - keep every field the backend gave us. Assign a
      // synthetic entry_id when legacy data comes in without one so duplicate
      // SKUs (post-2026-02) stay independently addressable.
      return {
        entry_id: entry.entry_id || makeEntryId(),
        sku: entry.sku,
        applies_to: entry.applies_to || ['new_order'],
        quantity: Number.isFinite(entry.quantity) && entry.quantity >= 1 ? entry.quantity : 1,
        source_rate_card_id: entry.source_rate_card_id || null,
        source_rate_card_name: entry.source_rate_card_name || null,
        source_item_id: entry.source_item_id || null,
      };
    });
  };

  // Parse existing per_unit_addon_skus into the same shape as setup fees.
  const parseExistingPerUnitAddons = (skus) => {
    if (!skus || !Array.isArray(skus)) return [];
    return skus.map(entry => {
      if (typeof entry === 'string') {
        return { entry_id: makeEntryId(), sku: entry, applies_to: ['new_order'], quantity: 1 };
      }
      return {
        entry_id: entry.entry_id || makeEntryId(),
        sku: entry.sku,
        applies_to: entry.applies_to || ['new_order'],
        quantity: Number.isFinite(entry.quantity) && entry.quantity >= 1 ? entry.quantity : 1,
        source_rate_card_id: entry.source_rate_card_id || null,
        source_rate_card_name: entry.source_rate_card_name || null,
        source_item_id: entry.source_item_id || null,
      };
    });
  };
  
  const [formData, setFormData] = useState({
    sales_sku: item?.sales_sku || '',
    bom_product_id: item?.bom_product_id || '',
    display_name: item?.display_name || '',
    description: item?.description || '',
    category: item?.category || (activeCategories[0]?.slug || 'vinyl'),
    is_active: item?.is_active ?? true,
    tags: item?.tags?.join(', ') || '',
    // Setup fee fields (SKU-based with per-SKU applicability) - one-time, amortized
    setup_fee_skus: parseExistingSetupFees(item?.setup_fee_skus),
    // Per-unit add-on fields (SKU-based) - per unit, NOT amortized
    per_unit_addon_skus: parseExistingPerUnitAddons(item?.per_unit_addon_skus),
    // Procurement flag. Default TRUE for new items so admins have to
    // opt-out for service lines (Graphic Check, Admin/Rush fees, …).
    // Existing items without the field are treated as TRUE.
    generate_purchase_line: item?.generate_purchase_line ?? true,
    // Quote gating — when true, this SKU has no fixed price. Any catalogue
    // reorder resolving to this SKU is routed through the Quote-request flow.
    requires_quote: item?.requires_quote ?? false,
    quote_instructions: item?.quote_instructions || '',
    // Reusable artwork templates linked to this SKU. Rendered as a
    // multi-select at the bottom of the item edit dialog. Templates
    // linked here auto-appear on the customer order under Artwork Upload.
    artwork_template_ids: item?.artwork_template_ids || [],
  });
  
  // State for setup fee SKU selection
  const [setupFeeCategory, setSetupFeeCategory] = useState('');
  const [setupFeeSearch, setSetupFeeSearch] = useState('');
  
  // State for per-unit add-on SKU selection
  const [addonCategory, setAddonCategory] = useState('');
  const [addonSearch, setAddonSearch] = useState('');
  
  const [tiers, setTiers] = useState(() => {
    // Initialize tiers sorted by min_qty ascending, with unique keys for UI tracking
    if (item?.tiers?.length) {
      return [...item.tiers]
        .map((t, i) => ({ ...t, _key: i, min_qty: t.min_qty, unit_price: t.unit_price }))
        .sort((a, b) => a.min_qty - b.min_qty);
    }
    return [{ _key: 0, min_qty: 100, unit_price: 0 }];
  });
  
  const [nextKey, setNextKey] = useState(() => item?.tiers?.length || 1);

  const addTier = () => {
    // Add new tier with next logical min_qty
    const sortedTiers = [...tiers].sort((a, b) => a.min_qty - b.min_qty);
    const lastTier = sortedTiers[sortedTiers.length - 1];
    setTiers([...tiers, { _key: nextKey, min_qty: (lastTier?.min_qty || 0) + 100, unit_price: lastTier?.unit_price || 0 }]);
    setNextKey(k => k + 1);
  };

  const removeTier = (key) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter(t => t._key !== key));
    }
  };

  const updateTier = (key, field, value) => {
    setTiers(tiers.map(t => 
      t._key === key 
        ? { ...t, [field]: field === 'unit_price' ? parseFloat(value) || 0 : parseInt(value) || 0 }
        : t
    ));
  };

  // Debounced BOM quote-gate preview — fires whenever the admin picks a
  // different BOM in the dropdown (saved OR unsaved). Reuses the shared
  // ``services.quote_gate`` helpers via ``/api/bom/quote-check`` so the
  // Sales editor badge can never disagree with the pricing engine.
  useEffect(() => {
    const bomId = formData.generate_purchase_line ? formData.bom_product_id : '';
    if (!bomId) {
      setBomQuoteGate({ quote_required: false, count: 0, components: [], loading: false });
      return;
    }
    let cancelled = false;
    setBomQuoteGate((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/bom/quote-check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ product_id: bomId }),
        });
        if (!res.ok) {
          if (!cancelled) setBomQuoteGate({ quote_required: false, count: 0, components: [], loading: false });
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setBomQuoteGate({
          quote_required: !!body.quote_required,
          count: body.count || 0,
          components: body.components || [],
          loading: false,
        });
      } catch {
        if (!cancelled) setBomQuoteGate({ quote_required: false, count: 0, components: [], loading: false });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [formData.bom_product_id, formData.generate_purchase_line]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Sort tiers by min_qty ascending before saving, strip _key field
    const sortedTiers = [...tiers]
      .filter(t => t.min_qty > 0)
      .sort((a, b) => a.min_qty - b.min_qty)
      .map(({ _key, ...rest }) => rest); // Remove _key field
    
    onSave({
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      tiers: sortedTiers,
      setup_fee_skus: formData.setup_fee_skus,
      per_unit_addon_skus: formData.per_unit_addon_skus,
      generate_purchase_line: !!formData.generate_purchase_line,
      // Service SKUs (generate_purchase_line=false) never carry a BOM link.
      bom_product_id: formData.generate_purchase_line ? formData.bom_product_id : '',
      requires_quote: !!formData.requires_quote,
      quote_instructions: (formData.quote_instructions || '').trim() || null,
      artwork_template_ids: formData.artwork_template_ids || [],
    });
  };
  
  // Helper: apply the per-pool family scope to a cross-card pool. This is
  // a client-side filter that runs after the network-level scope query.
  const applyFamilyScope = (pool, scope) => {
    if (!scope || scope === 'all') return pool;
    const effective = scope === 'current' ? activeFamily : scope;
    return pool.filter(i => (i.source_rate_card_product_family || 'vinyl') === effective);
  };

  // Helper: Get SKUs filtered by category — searches across active rate cards
  const getFilteredSkus = () => {
    if (!setupFeeCategory) return [];
    let pool;
    if (setupFeeSource === 'all') {
      pool = crossCardItems;
    } else if (setupFeeSource === 'current') {
      pool = crossCardItems.filter(i => i.source_rate_card_id === selectedCardId);
    } else {
      pool = crossCardItems.filter(i => i.source_rate_card_id === setupFeeSource);
    }
    // Apply family scope on top of the source scope
    pool = applyFamilyScope(pool, setupFeeFamilyScope);
    return pool
      .filter(i => i.category === setupFeeCategory && i.sales_sku !== formData.sales_sku)
      .filter(i => !setupFeeSearch || 
        i.display_name?.toLowerCase().includes(setupFeeSearch.toLowerCase()) ||
        i.sales_sku?.toLowerCase().includes(setupFeeSearch.toLowerCase())
      )
      .slice(0, 50);
  };
  
  // Helper: count how many entries already reference this SKU (used only
  // as an informational badge on the picker — duplicates are ALLOWED).
  const setupFeeSkuCount = (sku) => {
    return formData.setup_fee_skus.filter(s => s.sku === sku).length;
  };

  // Helper: Add SKU to setup fees with default applicability + source
  // tracking. Duplicates are allowed — each click appends an INDEPENDENT
  // entry with its own entry_id so the row can be configured (applies_to,
  // future qty, …) without affecting the other instances.
  const addSetupFeeSku = (sku) => {
    const skuItem = crossCardItems.find(i => i.sales_sku === sku);
    setFormData(prev => ({
      ...prev,
      setup_fee_skus: [...prev.setup_fee_skus, {
        entry_id: makeEntryId(),
        sku,
        applies_to: ['new_order'],
        quantity: 1,
        source_rate_card_id: skuItem?.source_rate_card_id || selectedCardId || null,
        source_rate_card_name: skuItem?.source_rate_card_name || selectedCardName || null,
        source_item_id: skuItem?.id || null,
      }]
    }));
  };

  // Helper: Remove a single setup-fee entry by entry_id (never removes
  // sibling entries that happen to share the same SKU).
  const removeSetupFeeEntry = (entryId) => {
    setFormData(prev => ({
      ...prev,
      setup_fee_skus: prev.setup_fee_skus.filter(s => s.entry_id !== entryId)
    }));
  };

  // Helper: Toggle applies_to flag on ONE setup-fee entry, keyed by entry_id.
  const toggleSetupFeeEntryAppliesTo = (entryId, flag) => {
    setFormData(prev => ({
      ...prev,
      setup_fee_skus: prev.setup_fee_skus.map(s => {
        if (s.entry_id !== entryId) return s;
        const current = s.applies_to || [];
        if (current.includes(flag)) {
          return { ...s, applies_to: current.filter(f => f !== flag) };
        }
        return { ...s, applies_to: [...current, flag] };
      })
    }));
  };

  // Helper: Set the per-entry quantity multiplier on ONE setup-fee entry.
  // A minimum of 1 is enforced (the model rejects <1 server-side too).
  const setSetupFeeEntryQuantity = (entryId, rawValue) => {
    const parsed = parseInt(rawValue, 10);
    const qty = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    setFormData(prev => ({
      ...prev,
      setup_fee_skus: prev.setup_fee_skus.map(s =>
        s.entry_id === entryId ? { ...s, quantity: qty } : s
      )
    }));
  };
  
  // Helper: Bulk set all SKUs to a specific applies_to
  const setAllAppliesTo = (appliesTo) => {
    setFormData(prev => ({
      ...prev,
      setup_fee_skus: prev.setup_fee_skus.map(s => ({ ...s, applies_to: appliesTo }))
    }));
  };
  
  // Helper: Get display info for a SKU — searches cross-card
  // Helper: Get display info for a linked SKU entry.
  // Uses the entry's stored source_rate_card_id when available so
  // cross-family linking is precise even when the same SKU code exists
  // in multiple rate cards. Falls back to a global sku-only search for
  // legacy entries that were saved before source_* was persisted.
  const getSkuDisplayInfo = (skuOrEntry) => {
    const entry = typeof skuOrEntry === 'string' ? { sku: skuOrEntry } : skuOrEntry;
    const sku = entry.sku;
    let skuItem = null;
    if (entry.source_rate_card_id) {
      skuItem =
        crossCardItems.find(
          i => i.sales_sku === sku && i.source_rate_card_id === entry.source_rate_card_id,
        ) ||
        allItems?.find(
          i => i.sales_sku === sku && (i.rate_card_id === entry.source_rate_card_id || i.source_rate_card_id === entry.source_rate_card_id),
        );
    }
    if (!skuItem) {
      skuItem = crossCardItems.find(i => i.sales_sku === sku) || allItems?.find(i => i.sales_sku === sku);
    }
    return skuItem 
      ? { name: skuItem.display_name, category: skuItem.category, sourceCard: skuItem.source_rate_card_name || entry.source_rate_card_name || '' } 
      : { name: sku, category: '?', sourceCard: entry.source_rate_card_name || '' };
  };
  
  // ============== Per-Unit Add-on Helpers ==============
  
  // Helper: Get SKUs filtered by category for per-unit addons — searches across active rate cards
  const getFilteredAddonSkus = () => {
    if (!addonCategory) return [];
    let pool;
    if (addonSource === 'all') {
      pool = crossCardItems;
    } else if (addonSource === 'current') {
      pool = crossCardItems.filter(i => i.source_rate_card_id === selectedCardId);
    } else {
      pool = crossCardItems.filter(i => i.source_rate_card_id === addonSource);
    }
    pool = applyFamilyScope(pool, addonFamilyScope);
    return pool
      .filter(i => i.category === addonCategory && i.sales_sku !== formData.sales_sku)
      .filter(i => !addonSearch || 
        i.display_name?.toLowerCase().includes(addonSearch.toLowerCase()) ||
        i.sales_sku?.toLowerCase().includes(addonSearch.toLowerCase())
      )
      .slice(0, 50);
  };
  
  // Helper: count how many entries already reference this SKU as a
  // per-unit add-on. Purely informational — duplicates are allowed.
  const addonSkuCount = (sku) => {
    return formData.per_unit_addon_skus.filter(s => s.sku === sku).length;
  };

  // Helper: Add SKU to per-unit addons with source tracking. Duplicates
  // are allowed — each click appends an INDEPENDENT entry keyed by
  // entry_id so the row can carry its own applies_to (and future qty)
  // configuration without collapsing into another instance.
  const addAddonSku = (sku) => {
    const skuItem = crossCardItems.find(i => i.sales_sku === sku);
    setFormData(prev => ({
      ...prev,
      per_unit_addon_skus: [...prev.per_unit_addon_skus, {
        entry_id: makeEntryId(),
        sku,
        applies_to: ['new_order'],
        quantity: 1,
        source_rate_card_id: skuItem?.source_rate_card_id || selectedCardId || null,
        source_rate_card_name: skuItem?.source_rate_card_name || selectedCardName || null,
        source_item_id: skuItem?.id || null,
      }]
    }));
  };

  // Helper: Remove ONE add-on entry by entry_id.
  const removeAddonEntry = (entryId) => {
    setFormData(prev => ({
      ...prev,
      per_unit_addon_skus: prev.per_unit_addon_skus.filter(s => s.entry_id !== entryId)
    }));
  };
  
  // Helper: Toggle applies_to flag on ONE add-on entry, keyed by entry_id.
  const toggleAddonEntryAppliesTo = (entryId, flag) => {
    setFormData(prev => ({
      ...prev,
      per_unit_addon_skus: prev.per_unit_addon_skus.map(s => {
        if (s.entry_id !== entryId) return s;
        const current = s.applies_to || [];
        if (current.includes(flag)) {
          return { ...s, applies_to: current.filter(f => f !== flag) };
        }
        return { ...s, applies_to: [...current, flag] };
      })
    }));
  };

  // Helper: Set the per-entry quantity multiplier on ONE add-on entry.
  const setAddonEntryQuantity = (entryId, rawValue) => {
    const parsed = parseInt(rawValue, 10);
    const qty = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    setFormData(prev => ({
      ...prev,
      per_unit_addon_skus: prev.per_unit_addon_skus.map(s =>
        s.entry_id === entryId ? { ...s, quantity: qty } : s
      )
    }));
  };
  
  // Helper: Bulk set all addon SKUs to a specific applies_to
  const setAllAddonAppliesTo = (appliesTo) => {
    setFormData(prev => ({
      ...prev,
      per_unit_addon_skus: prev.per_unit_addon_skus.map(s => ({ ...s, applies_to: appliesTo }))
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-border sticky top-0 bg-white">
          <h3 className="font-medium text-gray-900">
            {item ? 'Edit Price Item' : 'New Price Item'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales SKU</label>
              <input
                type="text"
                value={formData.sales_sku}
                onChange={(e) => setFormData({ ...formData, sales_sku: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                placeholder="12-STD-LP"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                {activeCategories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="generate-purchase-line-toggle">
              <input
                type="checkbox"
                checked={formData.generate_purchase_line}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    generate_purchase_line: on,
                    // Flipping OFF also nukes any lingering BOM link so a
                    // save can never leak the SKU onto a supplier PO.
                    bom_product_id: on ? prev.bom_product_id : '',
                  }));
                }}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">
                  Generate Purchase Line
                </div>
                <div className="text-xs text-slate-500 leading-snug mt-0.5">
                  On for production SKUs (linked to a BOM, generates supplier PO lines).
                  Turn off for service SKUs — Graphic Check, Design, Admin / Rush fees, etc. —
                  which should still price + invoice normally but never hit a supplier PO.
                </div>
              </div>
            </label>
          </div>

          {formData.generate_purchase_line && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">BOM Product</label>
              <FamilyScopePicker
                value={bomScope}
                onChange={onBomScopeChange || (() => {})}
                activeFamily={activeFamily}
                label="Family"
              />
            </div>
            <div className="relative">
              <select
                value={formData.bom_product_id}
                onChange={(e) => setFormData({ ...formData, bom_product_id: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Select BOM (optional for add-ons)...</option>
                {boms.map(bom => (
                  <option key={bom.product_id} value={bom.product_id}>
                    {bom.name} ({bom.product_id}) — [{(bom.product_family || 'vinyl').toUpperCase()}]
                  </option>
                ))}
              </select>
              {/* Cross-family warning chip when selected BOM is from another family */}
              {formData.bom_product_id && (() => {
                const selectedBom = boms.find(b => b.product_id === formData.bom_product_id);
                const bomFamily = selectedBom?.product_family;
                if (bomFamily && bomFamily !== activeFamily) {
                  return (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <CrossFamilyWarning valueFamily={bomFamily} activeFamily={activeFamily} />
                      <span className="text-[11px] text-amber-700">
                        Cross-family BOM link — pricing items from {bomFamily.toUpperCase()} family.
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            {boms.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No BOMs match the current scope. Try widening Family above or duplicate from Vinyl.
              </p>
            )}
            {/* BOM-driven Supplier Quote Required badge. Appears live when
                the selected BOM — saved OR unsaved — contains any
                procurement item flagged ``is_quotable=false``. Independent
                of the manual customer-quote override checkbox below. */}
            {formData.bom_product_id && bomQuoteGate.quote_required && (
              <div
                className="mt-2 flex items-start gap-2 p-2 border border-amber-300 bg-amber-50 rounded-md"
                data-testid="bom-quote-required-badge"
              >
                <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-amber-900">
                    Supplier Quote Required (from BOM)
                  </div>
                  <div className="text-[11px] text-amber-800 mt-0.5">
                    {(() => {
                      const names = (bomQuoteGate.components || []).map(c => c.procurement_item_name || c.supplier_sku);
                      const shown = names.slice(0, 3).join(', ');
                      const extra = names.length > 3 ? ` and ${names.length - 3} more` : '';
                      return names.length > 0
                        ? <>Procurement items missing a fixed price: <span className="font-medium">{shown}{extra}</span></>
                        : <>{bomQuoteGate.count} procurement item(s) in this BOM require a supplier quote.</>;
                    })()}
                  </div>
                </div>
              </div>
            )}
            {formData.bom_product_id && bomQuoteGate.loading && !bomQuoteGate.quote_required && (
              <p className="text-[11px] text-gray-400 mt-1" data-testid="bom-quote-check-loading">
                Checking BOM for quote requirements…
              </p>
            )}
          </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder='12" Standard Black LP'
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={2}
            />
          </div>
          
          {/* Price Tiers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Price Tiers</label>
              <span className="text-xs text-gray-500">Sorted by min qty (ascending)</span>
            </div>
            <div className="space-y-2">
              {[...tiers].sort((a, b) => a.min_qty - b.min_qty).map((tier) => (
                <div key={tier._key} className="flex items-center gap-2">
                  <input
                    type="number"
                    value={tier.min_qty}
                    onChange={(e) => updateTier(tier._key, 'min_qty', e.target.value)}
                    className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                    placeholder="Min qty"
                  />
                  <span className="text-gray-500">+</span>
                  <span className="text-gray-500">@</span>
                  <span className="text-gray-500">€</span>
                  <input
                    type="number"
                    step="0.01"
                    value={tier.unit_price}
                    onChange={(e) => updateTier(tier._key, 'unit_price', e.target.value)}
                    className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                    placeholder="Price"
                  />
                  {tiers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTier(tier._key)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTier}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + Add tier
              </button>
            </div>
          </div>
          
          {/* Setup Fee Section (SKU-based with per-SKU applicability) */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Setup Fee (SKU-based)</label>
              <span className="text-xs text-gray-500">One-time fee, amortized into unit price</span>
            </div>
            
            <div className="space-y-3">
              {/* Selected Setup Fee SKUs with per-SKU applicability */}
              {formData.setup_fee_skus.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-purple-700">Selected SKUs & Applicability</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAllAppliesTo(['new_order'])}
                        className="text-xs px-2 py-1 bg-white rounded border border-purple-200 hover:bg-purple-100 text-purple-700"
                      >
                        All → New
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllAppliesTo(['reorder'])}
                        className="text-xs px-2 py-1 bg-white rounded border border-purple-200 hover:bg-purple-100 text-purple-700"
                      >
                        All → Re-order
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllAppliesTo(['new_order', 'reorder'])}
                        className="text-xs px-2 py-1 bg-white rounded border border-purple-200 hover:bg-purple-100 text-purple-700"
                      >
                        All → Both
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {formData.setup_fee_skus.map(entry => {
                      const info = getSkuDisplayInfo(entry);
                      const hasNoApplicability = !entry.applies_to || entry.applies_to.length === 0;
                      return (
                        <div key={entry.entry_id} className={`flex items-center gap-3 p-2 bg-white rounded border ${hasNoApplicability ? 'border-amber-300 bg-amber-50' : 'border-purple-200'}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">{info.name}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">({entry.sku})</span>
                            </div>
                            {(entry.source_rate_card_name || info.sourceCard) && (entry.source_rate_card_name || info.sourceCard) !== selectedCardName && (
                              <div className="text-[10px] text-blue-500 mt-0.5">from: {entry.source_rate_card_name || info.sourceCard}</div>
                            )}
                            {hasNoApplicability && (
                              <div className="text-xs text-amber-600 mt-0.5">Warning: no order type selected</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">×</span>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={entry.quantity ?? 1}
                                onChange={(e) => setSetupFeeEntryQuantity(entry.entry_id, e.target.value)}
                                className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs text-right"
                                title="Charge this fee this many times (e.g. 2 for gatefold double vinyl)"
                                data-testid={`setup-fee-qty-${entry.entry_id}`}
                              />
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={entry.applies_to?.includes('new_order') || false}
                                onChange={() => toggleSetupFeeEntryAppliesTo(entry.entry_id, 'new_order')}
                                className="rounded border-gray-300 text-purple-600"
                              />
                              <span className="text-xs text-gray-600">New</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={entry.applies_to?.includes('reorder') || false}
                                onChange={() => toggleSetupFeeEntryAppliesTo(entry.entry_id, 'reorder')}
                                className="rounded border-gray-300 text-purple-600"
                              />
                              <span className="text-xs text-gray-600">Re-order</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => removeSetupFeeEntry(entry.entry_id)}
                              className="p-1 text-gray-400 hover:text-red-500"
                              title="Remove this entry"
                              data-testid={`remove-setup-fee-entry-${entry.entry_id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Source Rate Card Filter */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-gray-500">
                    1. Source Rate Card
                  </label>
                  <FamilyScopePicker
                    value={setupFeeFamilyScope}
                    onChange={setSetupFeeFamilyScope}
                    activeFamily={activeFamily}
                    label="Family"
                  />
                </div>
                <select
                  value={setupFeeSource}
                  onChange={(e) => { setSetupFeeSource(e.target.value); setSetupFeeCategory(''); setSetupFeeSearch(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="setup-fee-source"
                >
                  <option value="all">
                    {setupFeeFamilyScope === 'all'
                      ? 'All active rate cards (every family)'
                      : `All active ${(setupFeeFamilyScope === 'current' ? activeFamily : setupFeeFamilyScope).toUpperCase()} rate cards`}
                  </option>
                  {sourceRateCards
                    .filter(rc => {
                      if (setupFeeFamilyScope === 'all') return true;
                      const fam = setupFeeFamilyScope === 'current' ? activeFamily : setupFeeFamilyScope;
                      return (rc.product_family || 'vinyl') === fam;
                    })
                    .map(rc => (
                      <option key={rc.id} value={rc.id}>
                        {rc.name} [{(rc.product_family || 'vinyl').toUpperCase()}]{rc.id === selectedCardId ? ' (current)' : ''}
                      </option>
                    ))}
                </select>
              </div>
              
              {/* Category Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">2. Select Category</label>
                <select
                  value={setupFeeCategory}
                  onChange={(e) => { setSetupFeeCategory(e.target.value); setSetupFeeSearch(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="setup-fee-category"
                >
                  <option value="">Select a category...</option>
                  {getCrossCardCategories(setupFeeSource).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* SKU Search & Selection */}
              {setupFeeCategory && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">3. Add Setup Fee SKUs</label>
                  <input
                    type="text"
                    placeholder="Search SKUs..."
                    value={setupFeeSearch}
                    onChange={(e) => setSetupFeeSearch(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
                    data-testid="setup-fee-search"
                  />
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                    {crossCardLoading ? (
                      <div className="p-2 text-sm text-gray-500 text-center">Loading...</div>
                    ) : getFilteredSkus().length === 0 ? (
                      <div className="p-2 text-sm text-gray-500 text-center">No SKUs found in this category</div>
                    ) : (
                      getFilteredSkus().map(skuItem => {
                        const timesAdded = setupFeeSkuCount(skuItem.sales_sku);
                        return (
                        <div
                          key={`${skuItem.source_rate_card_id}-${skuItem.sales_sku}`}
                          onClick={() => addSetupFeeSku(skuItem.sales_sku)}
                          className="p-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                          data-testid={`setup-fee-sku-${skuItem.sales_sku}`}
                          title={timesAdded > 0
                            ? `Already added ${timesAdded}× — click again to add another instance`
                            : 'Click to add as a setup fee'}
                        >
                          <div>
                            <div className="text-sm font-medium">{skuItem.display_name}</div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{skuItem.sales_sku}</span>
                              {skuItem.source_rate_card_name && skuItem.source_rate_card_id !== selectedCardId && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{skuItem.source_rate_card_name}</span>
                              )}
                            </div>
                          </div>
                          {timesAdded > 0 && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold"
                              data-testid={`setup-fee-count-${skuItem.sales_sku}`}
                            >
                              ×{timesAdded}
                            </span>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              
              {/* Help text */}
              {formData.setup_fee_skus.length === 0 && (
                <p className="text-xs text-gray-400">
                  Select a source rate card and category, then add SKUs to use as setup fees. SKUs can come from any active rate card.
                </p>
              )}
            </div>
          </div>
          
          {/* Per-Unit Add-on (SKU-based) */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Per-Unit Add-on (SKU-based)</label>
              <span className="text-xs text-gray-500">Cost per unit, NOT amortized</span>
            </div>
            
            <div className="space-y-3">
              {/* Selected Per-Unit Add-on SKUs */}
              {formData.per_unit_addon_skus.length > 0 && (
                <div className="bg-teal-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-teal-700">Selected Add-on SKUs & Applicability</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAllAddonAppliesTo(['new_order'])}
                        className="text-xs px-2 py-1 bg-white rounded border border-teal-200 hover:bg-teal-100 text-teal-700"
                      >
                        All → New
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllAddonAppliesTo(['reorder'])}
                        className="text-xs px-2 py-1 bg-white rounded border border-teal-200 hover:bg-teal-100 text-teal-700"
                      >
                        All → Re-order
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllAddonAppliesTo(['new_order', 'reorder'])}
                        className="text-xs px-2 py-1 bg-white rounded border border-teal-200 hover:bg-teal-100 text-teal-700"
                      >
                        All → Both
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {formData.per_unit_addon_skus.map(entry => {
                      const info = getSkuDisplayInfo(entry);
                      const hasNoApplicability = !entry.applies_to || entry.applies_to.length === 0;
                      return (
                        <div key={entry.entry_id} className={`flex items-center gap-3 p-2 bg-white rounded border ${hasNoApplicability ? 'border-amber-300 bg-amber-50' : 'border-teal-200'}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">{info.name}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">({entry.sku})</span>
                            </div>
                            {(entry.source_rate_card_name || info.sourceCard) && (entry.source_rate_card_name || info.sourceCard) !== selectedCardName && (
                              <div className="text-[10px] text-blue-500 mt-0.5">from: {entry.source_rate_card_name || info.sourceCard}</div>
                            )}
                            {hasNoApplicability && (
                              <div className="text-xs text-amber-600 mt-0.5">Warning: no order type selected</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">×</span>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={entry.quantity ?? 1}
                                onChange={(e) => setAddonEntryQuantity(entry.entry_id, e.target.value)}
                                className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs text-right"
                                title="Multiplier on top of the per-unit charge (e.g. 2 for gatefold double vinyl)"
                                data-testid={`addon-qty-${entry.entry_id}`}
                              />
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={entry.applies_to?.includes('new_order') || false}
                                onChange={() => toggleAddonEntryAppliesTo(entry.entry_id, 'new_order')}
                                className="rounded border-gray-300 text-teal-600"
                              />
                              <span className="text-xs text-gray-600">New</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={entry.applies_to?.includes('reorder') || false}
                                onChange={() => toggleAddonEntryAppliesTo(entry.entry_id, 'reorder')}
                                className="rounded border-gray-300 text-teal-600"
                              />
                              <span className="text-xs text-gray-600">Re-order</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => removeAddonEntry(entry.entry_id)}
                              className="p-1 text-gray-400 hover:text-red-500"
                              title="Remove this entry"
                              data-testid={`remove-addon-entry-${entry.entry_id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Source Rate Card Filter for Add-ons */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-gray-500">
                    1. Source Rate Card
                  </label>
                  <FamilyScopePicker
                    value={addonFamilyScope}
                    onChange={setAddonFamilyScope}
                    activeFamily={activeFamily}
                    label="Family"
                  />
                </div>
                <select
                  value={addonSource}
                  onChange={(e) => { setAddonSource(e.target.value); setAddonCategory(''); setAddonSearch(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="addon-source"
                >
                  <option value="all">
                    {addonFamilyScope === 'all'
                      ? 'All active rate cards (every family)'
                      : `All active ${(addonFamilyScope === 'current' ? activeFamily : addonFamilyScope).toUpperCase()} rate cards`}
                  </option>
                  {sourceRateCards
                    .filter(rc => {
                      if (addonFamilyScope === 'all') return true;
                      const fam = addonFamilyScope === 'current' ? activeFamily : addonFamilyScope;
                      return (rc.product_family || 'vinyl') === fam;
                    })
                    .map(rc => (
                      <option key={rc.id} value={rc.id}>
                        {rc.name} [{(rc.product_family || 'vinyl').toUpperCase()}]{rc.id === selectedCardId ? ' (current)' : ''}
                      </option>
                    ))}
                </select>
              </div>
              
              {/* Category Filter for Add-ons */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">2. Select Category</label>
                <select
                  value={addonCategory}
                  onChange={(e) => { setAddonCategory(e.target.value); setAddonSearch(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select a category...</option>
                  {getCrossCardCategories(addonSource).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* SKU Search & Selection for Add-ons */}
              {addonCategory && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">3. Search & Select Add-on SKUs</label>
                  <input
                    type="text"
                    value={addonSearch}
                    onChange={(e) => setAddonSearch(e.target.value)}
                    placeholder="Search SKUs..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
                  />
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {crossCardLoading ? (
                      <div className="p-3 text-center text-sm text-gray-500">Loading...</div>
                    ) : getFilteredAddonSkus().length === 0 ? (
                      <div className="p-3 text-center text-sm text-gray-500">
                        No SKUs found in this category
                      </div>
                    ) : (
                      getFilteredAddonSkus().map(skuItem => {
                        const timesAdded = addonSkuCount(skuItem.sales_sku);
                        return (
                        <div
                          key={`${skuItem.source_rate_card_id}-${skuItem.sales_sku}`}
                          onClick={() => addAddonSku(skuItem.sales_sku)}
                          className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50"
                          data-testid={`addon-sku-${skuItem.sales_sku}`}
                          title={timesAdded > 0
                            ? `Already added ${timesAdded}× — click again to add another instance`
                            : 'Click to add as a per-unit add-on'}
                        >
                          <div>
                            <div className="text-sm font-medium">{skuItem.display_name}</div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{skuItem.sales_sku}</span>
                              {skuItem.source_rate_card_name && skuItem.source_rate_card_id !== selectedCardId && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{skuItem.source_rate_card_name}</span>
                              )}
                            </div>
                          </div>
                          {timesAdded > 0 && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-semibold"
                              data-testid={`addon-count-${skuItem.sales_sku}`}
                            >
                              ×{timesAdded}
                            </span>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              
              {/* Help text */}
              {formData.per_unit_addon_skus.length === 0 && (
                <p className="text-xs text-gray-400">
                  Select a source rate card and category, then add SKUs for per-unit add-ons. These costs are added to EACH unit (quantity x add-on price).
                </p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="12inch, standard, black"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
          </div>

          {/* Quote gating — this SKU has no fixed price. Customers must
              request a quote through the catalogue Reorder dialog, and
              admin manually prices the order via the Invoice Basis UI.
              NOTE: this is the MANUAL override. A BOM-driven quote
              requirement (shown as the yellow badge below the BOM
              selector above) is independent and never writes to this
              flag. The effective state is reflected in the read-only
              row immediately below the checkbox. */}
          <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg space-y-2" data-testid="requires-quote-block">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_quote"
                checked={!!formData.requires_quote}
                onChange={(e) => setFormData({ ...formData, requires_quote: e.target.checked })}
                className="rounded border-amber-400"
                data-testid="requires-quote-checkbox"
              />
              <label htmlFor="requires_quote" className="text-sm font-medium text-amber-900">
                Requires Customer Quote (manual override)
              </label>
            </div>
            {/* Read-only Effective Quote Status — reflects manual flag OR
                BOM-driven requirement. Never auto-mutates the manual
                flag. */}
            {(() => {
              const manual = !!formData.requires_quote;
              const bomQuote = !!bomQuoteGate.quote_required;
              let label;
              let testid;
              if (manual && bomQuote) { label = 'Both (Manual + BOM)'; testid = 'both'; }
              else if (manual) { label = 'Manual only'; testid = 'manual'; }
              else if (bomQuote) { label = 'BOM only'; testid = 'bom'; }
              else { label = 'None'; testid = 'none'; }
              const chipClass = testid === 'none'
                ? 'bg-white border-gray-300 text-gray-600'
                : 'bg-amber-200 border-amber-400 text-amber-900';
              return (
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-amber-900 font-medium">Effective Quote Status:</span>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 border rounded ${chipClass}`}
                    data-testid={`effective-quote-status-${testid}`}
                  >
                    {label}
                  </span>
                </div>
              );
            })()}
            <p className="text-[11px] text-amber-800 leading-snug">
              Manual override: when enabled, the customer never sees a price for this SKU.
              Independent of the BOM-driven supplier quote badge above — either trigger alone
              is enough to send the order through the quote flow. Toggling this does NOT change
              the BOM requirement, and the BOM requirement does NOT change this flag.
            </p>
            <p className="text-[11px] text-amber-800 leading-snug bg-amber-100 border border-amber-300 rounded px-2 py-1.5">
              <strong>How it triggers:</strong> the quote flow only kicks in when a catalogue
              entry&apos;s pricing path <em>actually references this SKU</em> — either through explicit
              catalogue SKU links, a Quick-Order bundle containing this SKU, or a Visual-Builder
              flow whose step maps to it. Flagging a standalone SKU that no catalogue entry or
              flow references will NOT affect existing reorders.
            </p>
            {formData.requires_quote && (
              <div>
                <label className="block text-xs text-amber-900 mb-1">
                  Default quote instructions (internal note — shown to admin)
                </label>
                <textarea
                  value={formData.quote_instructions || ''}
                  onChange={(e) => setFormData({ ...formData, quote_instructions: e.target.value })}
                  className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white"
                  rows={3}
                  placeholder="e.g. Confirm print colour with client before pricing. Always add 5% for freight."
                  data-testid="quote-instructions-textarea"
                />
              </div>
            )}
          </div>

          {/* Linked Artwork Templates — resolved on the customer order
              via Sales SKU → artwork_template_ids[]. Downloading a
              template does NOT count as uploaded artwork. */}
          <ArtworkTemplatesPicker
            value={formData.artwork_template_ids || []}
            onChange={(ids) => setFormData({ ...formData, artwork_template_ids: ids })}
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Category Management Modal Component
function CategoryModal({ categories, onClose, onRefresh }) {
  const [editingCategory, setEditingCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const method = editingCategory ? 'PATCH' : 'POST';
      const url = editingCategory 
        ? `${API_URL}/api/sales/categories/${editingCategory.id}`
        : `${API_URL}/api/sales/categories`;
      
      await safeFetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      setShowForm(false);
      setEditingCategory(null);
      setFormData({ name: '', slug: '', description: '', sort_order: 0 });
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setFormData({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || '',
      sort_order: cat.sort_order
    });
    setShowForm(true);
  };

  const handleDelete = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    
    try {
      await safeFetch(`${API_URL}/api/sales/categories/${catId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await safeFetch(`${API_URL}/api/sales/categories/${cat.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !cat.is_active })
      });
      
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-medium text-gray-900">Manage Sales Categories</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {showForm ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      setFormData({ ...formData, name, slug: editingCategory ? formData.slug : slug });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingCategory(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="mb-4">
                <button
                  onClick={() => { setShowForm(true); setEditingCategory(null); setFormData({ name: '', slug: '', description: '', sort_order: (categories.length + 1) * 10 }); }}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Category
                </button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Order</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Slug</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">Active</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {categories.map(cat => (
                    <tr key={cat.id} className={`hover:bg-gray-50 ${!cat.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2 text-gray-600">{cat.sort_order}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{cat.name}</td>
                      <td className="px-3 py-2 font-mono text-gray-600">{cat.slug}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => handleToggleActive(cat)}>
                          {cat.is_active ? (
                            <Check className="w-4 h-4 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-4 h-4 text-red-500 mx-auto" />
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleEdit(cat)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Edit className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(cat.id)}
                          className="p-1 hover:bg-gray-100 rounded ml-1"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Price Items View Component (Configuration)
function PriceItemsView({ 
  items, 
  filteredItems, 
  itemsLoading, 
  searchTerm, 
  setSearchTerm, 
  handleExport, 
  setEditingItem, 
  setShowItemModal, 
  handleDeleteItem,
  handleCloneItem,
  handleToggleItemActive,
  savingItemIds,
  onImportClick,
  onDownloadTemplate,
  onOpenTemplatesDialog,
  // { sales_sku: string[] } — templates inherited from linked sub-SKUs
  // (setup fees + per-unit add-ons). Merged with direct
  // artwork_template_ids to produce the Templates-column badge.
  crossCardTemplateMap = {},
  // { [bom_product_id]: { quote_required, count, components } } —
  // computed in the parent by fan-out POSTs to /api/bom/quote-check.
  // Drives the Quote column (Manual / BOM / Both / —).
  bomQuoteGateByProductId = {},
}) {
  // Given a rate item, resolve the *inherited* template ids from its
  // linked setup_fee_skus and per_unit_addon_skus. Direct
  // artwork_template_ids on the item itself are excluded here so the
  // caller can compute (direct + inherited) totals with an explicit
  // dedupe pass.
  const getInheritedTemplateIds = (item) => {
    const linkedSkus = [];
    const pushSku = (entry) => {
      if (!entry) return;
      const sku = typeof entry === 'string' ? entry : entry?.sku;
      if (sku) linkedSkus.push(sku);
    };
    (item?.setup_fee_skus || []).forEach(pushSku);
    (item?.per_unit_addon_skus || []).forEach(pushSku);
    if (linkedSkus.length === 0) return [];

    const acc = new Set();
    for (const sku of linkedSkus) {
      const tids = crossCardTemplateMap[sku];
      if (Array.isArray(tids)) tids.forEach((tid) => acc.add(tid));
    }
    return Array.from(acc);
  };
  return (
    <>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <span className="text-sm text-gray-600">{items.length} items</span>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
              data-testid="search-price-items"
            />
          </div>
          <button
            onClick={onImportClick}
            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            data-testid="import-price-items"
          >
            <FileUp className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            data-testid="export-rate-card"
          >
            <FileDown className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => { setEditingItem(null); setShowItemModal(true); }}
            className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            data-testid="add-price-item"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>
      </div>
      {itemsLoading ? (
        <div className="px-4 py-8 text-center text-gray-500">Loading items...</div>
      ) : filteredItems.length === 0 ? (
        <div className="px-4 py-8 text-center text-gray-500">
          {searchTerm ? 'No items match your search' : 'No items in this rate card'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">SKU</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Product</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">BOM</th>
                <th className="px-4 py-2 text-center font-medium text-gray-600">Quote</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Price Tiers</th>
                <th className="px-4 py-2 text-center font-medium text-gray-600">Templates</th>
                <th className="px-4 py-2 text-center font-medium text-gray-600">Status</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredItems.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-gray-600">{item.sales_sku}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                      {item.display_name}
                      {item.generate_purchase_line === false && (
                        <span
                          className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-medium tracking-wide uppercase"
                          title="Service SKU — never generates a supplier PO line"
                          data-testid="service-sku-badge"
                        >
                          Service
                        </span>
                      )}
                    </div>
                    {item.category && (
                      <span className="text-xs text-gray-500">{item.category}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600 text-xs">
                    {item.generate_purchase_line === false
                      ? <span className="text-slate-400 italic">— (service)</span>
                      : (item.bom_product_name || item.bom_product_id)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {(() => {
                      const manual = !!item.requires_quote;
                      const gate = item.generate_purchase_line !== false && item.bom_product_id
                        ? bomQuoteGateByProductId[item.bom_product_id]
                        : null;
                      const bomQuote = !!(gate && gate.quote_required);
                      if (!manual && !bomQuote) {
                        return (
                          <span className="text-gray-300 select-none" data-testid={`item-quote-none-${item.sales_sku}`}>—</span>
                        );
                      }
                      let label = '';
                      let testid = '';
                      if (manual && bomQuote) { label = 'Both'; testid = 'both'; }
                      else if (manual) { label = 'Manual'; testid = 'manual'; }
                      else { label = 'BOM'; testid = 'bom'; }
                      const compNames = (gate?.components || []).map(c => c.procurement_item_name || c.supplier_sku);
                      const tipParts = [];
                      if (manual) tipParts.push('Manual customer-quote override is ON');
                      if (bomQuote) tipParts.push(`BOM requires supplier quote: ${compNames.slice(0, 3).join(', ')}${compNames.length > 3 ? ` and ${compNames.length - 3} more` : ''}`);
                      return (
                        <span
                          className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded text-[11px] font-medium"
                          title={tipParts.join(' · ')}
                          data-testid={`item-quote-${testid}-${item.sales_sku}`}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {item.tiers?.slice(0, 3).map((tier, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">
                          {tier.min_qty}+ @ €{tier.unit_price}
                        </span>
                      ))}
                      {item.tiers?.length > 3 && (
                        <span className="text-xs text-gray-400">+{item.tiers.length - 3}</span>
                      )}
                      {item.setup_fee_skus && item.setup_fee_skus.length > 0 && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs" title={`Setup fee SKUs: ${item.setup_fee_skus.map(s => typeof s === 'string' ? s : s.sku).join(', ')}`}>
                          +{item.setup_fee_skus.length} setup
                        </span>
                      )}
                      {item.per_unit_addon_skus && item.per_unit_addon_skus.length > 0 && (
                        <span className="px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-xs" title={`Per-unit add-on SKUs: ${item.per_unit_addon_skus.map(s => typeof s === 'string' ? s : s.sku).join(', ')}`}>
                          +{item.per_unit_addon_skus.length} add-on
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {(() => {
                      // Direct = templates linked on the item itself.
                      // Inherited = templates linked on any of the SKUs
                      // referenced in setup_fee_skus / per_unit_addon_skus.
                      // We dedupe by id so a template linked both
                      // directly and via a sub-SKU counts once.
                      const directIds = item.artwork_template_ids || [];
                      const inheritedIds = getInheritedTemplateIds(item);
                      const combined = new Set([...directIds, ...inheritedIds]);
                      const totalCount = combined.size;
                      const directCount = directIds.length;
                      const inheritedOnlyCount = totalCount - directCount;

                      if (totalCount === 0) {
                        return (
                          <span
                            className="text-gray-300 select-none"
                            title="No artwork templates linked"
                            data-testid={`item-templates-none-${item.sales_sku}`}
                          >
                            —
                          </span>
                        );
                      }

                      // Only inherited (no direct link) → subtle purple
                      // badge with an "inherited" affordance so the
                      // admin can distinguish inherited-only rows at a
                      // glance. Clicking still opens the direct-edit
                      // dialog on the parent SKU (empty state), which
                      // lets the admin promote an inherited template to
                      // a direct link if they want.
                      const hasInherited = inheritedOnlyCount > 0;
                      const tooltipParts = [];
                      if (directCount > 0) tooltipParts.push(`${directCount} direct`);
                      if (inheritedOnlyCount > 0) tooltipParts.push(`${inheritedOnlyCount} inherited from linked SKUs`);
                      const tooltip = tooltipParts.join(' + ') || 'Linked artwork templates';

                      return (
                        <button
                          type="button"
                          onClick={() => onOpenTemplatesDialog?.(item)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                            directCount === 0
                              ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                              : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                          }`}
                          title={tooltip}
                          data-testid={`item-templates-badge-${item.sales_sku}`}
                        >
                          <FileText className="w-3 h-3" />
                          {totalCount} {totalCount === 1 ? 'template' : 'templates'}
                          {hasInherited && directCount > 0 && (
                            <span
                              className="ml-0.5 px-1 py-0 rounded bg-purple-100 text-purple-700 text-[10px] font-semibold"
                              data-testid={`item-templates-inherited-badge-${item.sales_sku}`}
                            >
                              +{inheritedOnlyCount} inh.
                            </span>
                          )}
                          {hasInherited && directCount === 0 && (
                            <span
                              className="ml-0.5 text-[10px] font-semibold uppercase tracking-wide"
                              data-testid={`item-templates-inherited-only-${item.sales_sku}`}
                            >
                              inh.
                            </span>
                          )}
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => handleToggleItemActive?.(item)}
                      disabled={savingItemIds?.has(item.id)}
                      className={`p-1 rounded-full transition-colors ${
                          savingItemIds?.has(item.id)
                              ? 'opacity-40 cursor-wait'
                              : 'hover:bg-gray-100 cursor-pointer'
                      }`}
                      title={item.is_active
                          ? 'Click to deactivate (won\'t appear in new orders)'
                          : 'Click to activate'}
                      aria-label={item.is_active ? 'Deactivate item' : 'Activate item'}
                      data-testid={`toggle-item-active-${item.sales_sku}`}
                    >
                      {item.is_active ? (
                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-4 h-4 text-gray-400 mx-auto" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => { setEditingItem(item); setShowItemModal(true); }}
                      className="p-1 hover:bg-gray-100 rounded"
                      data-testid={`edit-item-${item.sales_sku}`}
                    >
                      <Edit className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleCloneItem?.(item.id)}
                      className="p-1 hover:bg-indigo-50 rounded ml-1 text-indigo-600"
                      title="Clone item"
                      data-testid={`clone-item-${item.sales_sku}`}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="p-1 hover:bg-gray-100 rounded ml-1"
                      data-testid={`delete-item-${item.sales_sku}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// Margin Analysis View Component (Analytics - Single Product Focus)
function MarginAnalysisView({ selectedCard, items, getMarginColor }) {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(250);
  const [marginData, setMarginData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getToken = () => localStorage.getItem('token');

  // Available quantity options
  const quantityOptions = [100, 250, 500, 750, 1000, 1500, 2000];

  // Fetch margin for selected product and quantity
  const fetchMargin = useCallback(async () => {
    if (!selectedCard?.id || !selectedProduct) {
      setMarginData(null);
      return;
    }
    
    try {
      setLoading(true);
      const data = await safeFetch(
        `${API_URL}/api/sales/rate-cards/${selectedCard.id}/margin-preview?quantity=${selectedQuantity}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      
      // Find the specific product's margin data
      const productData = data?.items?.find(i => i.sales_sku === selectedProduct);
      setMarginData(productData || null);
    } catch (err) {
      console.error('Margin fetch error:', err);
      setMarginData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCard?.id, selectedProduct, selectedQuantity]);

  useEffect(() => {
    fetchMargin();
  }, [fetchMargin]);

  // Auto-select first product if none selected
  useEffect(() => {
    if (items.length > 0 && !selectedProduct) {
      setSelectedProduct(items[0].sales_sku);
    }
  }, [items, selectedProduct]);

  const selectedItem = items.find(i => i.sales_sku === selectedProduct);

  return (
    <div className="p-6">
      <div className="max-w-md mx-auto space-y-6">
        {/* Product Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Product
          </label>
          <select
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="margin-product-select"
          >
            <option value="">Choose a product...</option>
            {items.map(item => (
              <option key={item.sales_sku} value={item.sales_sku}>
                {item.display_name}
              </option>
            ))}
          </select>
        </div>

        {/* Quantity Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity
          </label>
          <select
            value={selectedQuantity}
            onChange={(e) => setSelectedQuantity(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="margin-quantity-select"
          >
            {quantityOptions.map(qty => (
              <option key={qty} value={qty}>{qty} units</option>
            ))}
          </select>
        </div>

        {/* Results Display */}
        {loading ? (
          <div className="py-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
            Calculating margin...
          </div>
        ) : selectedProduct && marginData ? (
          <div className="bg-gray-50 rounded-xl p-6 space-y-4" data-testid="margin-results">
            {/* Product Info */}
            <div className="text-center border-b border-border pb-4">
              <h4 className="font-semibold text-gray-900">{marginData.display_name}</h4>
              <p className="text-sm text-gray-500 font-mono">{marginData.sales_sku}</p>
            </div>

            {/* Margin Breakdown */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Sales Total</span>
                <span className="text-lg font-semibold text-gray-900" data-testid="margin-sales-total">
                  €{marginData.sales_total?.toFixed(2) || '0.00'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">BOM Cost</span>
                <span className="text-lg font-semibold text-gray-600" data-testid="margin-bom-cost">
                  €{marginData.bom_cost_total?.toFixed(2) || '—'}
                </span>
              </div>
              
              <div className="border-t border-gray-300 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-900 font-medium">Margin</span>
                  <span className="text-xl font-bold text-gray-900" data-testid="margin-absolute">
                    €{marginData.margin_absolute?.toFixed(2) || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Margin Percentage Badge */}
            {marginData.margin_percent !== null && (
              <div className="flex justify-center pt-2">
                <span 
                  className={`px-4 py-2 rounded-full text-lg font-bold ${getMarginColor(marginData.margin_percent)}`}
                  data-testid="margin-percent"
                >
                  {marginData.margin_percent?.toFixed(1)}%
                </span>
              </div>
            )}

            {/* Unit Price Info */}
            <div className="bg-white rounded-lg p-3 mt-4 text-sm text-gray-500 text-center">
              Unit price at {selectedQuantity} qty: €{marginData.unit_price?.toFixed(2) || '—'}
            </div>
          </div>
        ) : selectedProduct && !marginData ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <AlertCircle className="w-5 h-5 text-amber-500 mx-auto mb-2" />
            <p className="text-amber-700 text-sm">
              No margin data available for this product. Make sure it has a linked BOM with valid cost data.
            </p>
          </div>
        ) : (
          <div className="py-12 text-center text-gray-400">
            <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Select a product to view margin analysis</p>
          </div>
        )}

        {/* Info Note */}
        <div className="text-xs text-gray-400 text-center pt-4">
          Margin = Sales Price - BOM Cost
        </div>
      </div>
    </div>
  );
}


// Import Modal Component
function ImportModal({
  selectedCard,
  importFile,
  importPreview,
  importLoading,
  importCommitting,
  fileInputRef,
  onFileSelect,
  onPreview,
  onCommit,
  onReset,
  onDownloadTemplate,
  onClose,
  importMode,
  setImportMode,
  deleteMissing,
  setDeleteMissing
}) {
  const [previewTab, setPreviewTab] = useState('summary'); // 'summary', 'all', 'errors', 'delete'

  const getActionBadge = (action) => {
    switch (action) {
      case 'create':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Create</span>;
      case 'update':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Update</span>;
      case 'skip':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Skip</span>;
      case 'error':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Error</span>;
      default:
        return null;
    }
  };

  const filteredRows = importPreview?.rows?.filter(row => {
    if (previewTab === 'errors') return row.action === 'error';
    if (previewTab === 'all') return true;
    return false;
  }) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import Price Items</h2>
            <p className="text-sm text-gray-500">{selectedCard?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!importPreview ? (
            // Step 1: Upload file
            <div className="space-y-6">
              {/* Template download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Download Template</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Start with our template to ensure your data is formatted correctly.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => onDownloadTemplate('csv')}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50"
                  >
                    <Download className="w-4 h-4" />
                    CSV Template
                  </button>
                  <button
                    onClick={() => onDownloadTemplate('xlsx')}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50"
                  >
                    <Download className="w-4 h-4" />
                    Excel Template
                  </button>
                </div>
              </div>

              {/* File upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={onFileSelect}
                  className="hidden"
                />
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  {importFile ? (
                    <span className="font-medium text-gray-900">{importFile.name}</span>
                  ) : (
                    'Drag and drop your file here, or click to browse'
                  )}
                </p>
                <p className="text-sm text-gray-500 mb-4">Supports CSV and Excel files</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Select File
                </button>
              </div>

              {/* Import Mode Options */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Import Mode</h3>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="update_and_add"
                      checked={importMode === 'update_and_add'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-800">Update existing + Add new</div>
                      <div className="text-sm text-gray-500">Update items that match by SKU, create new items for SKUs not found</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="add_only"
                      checked={importMode === 'add_only'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-800">Add new only</div>
                      <div className="text-sm text-gray-500">Only create new items, skip SKUs that already exist</div>
                    </div>
                  </label>
                </div>
                
                {/* Delete Missing Option */}
                <div className="mt-4 pt-4 border-t border-border">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deleteMissing}
                      onChange={(e) => setDeleteMissing(e.target.checked)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-800 flex items-center gap-2">
                        Delete missing items
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Destructive</span>
                      </div>
                      <div className="text-sm text-gray-500">Remove items from the rate card that are not present in the import file</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Format info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Wide Tier Format</h3>
                <p className="text-sm text-gray-600 mb-3">One row per product with tier prices in columns:</p>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <div><code className="bg-gray-200 px-1 rounded">sales_sku</code> - Unique identifier</div>
                  <div><code className="bg-gray-200 px-1 rounded">display_name</code> - Product name</div>
                  <div><code className="bg-gray-200 px-1 rounded">category</code> - Product category</div>
                  <div><code className="bg-gray-200 px-1 rounded">show_order</code> - Sort order</div>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-medium text-gray-700 mb-1">Tier Columns:</p>
                  <div className="flex flex-wrap gap-1">
                    {['tier_100', 'tier_250', 'tier_500', 'tier_1000', 'tier_2000'].map(col => (
                      <code key={col} className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">{col}</code>
                    ))}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-medium text-gray-700 mb-1">Setup Fee Column (per-SKU applicability):</p>
                  <div className="flex flex-wrap gap-1">
                    <code className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">setup_fee_skus</code>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Format: "SKU:new,reorder;SKU2:new" where "new" = new orders, "reorder" = re-orders</p>
                  <p className="text-xs text-gray-500">Example: "MASTERING-STD:new;LACQUER-12:new,reorder" — MASTERING applies only to new, LACQUER to both</p>
                </div>
              </div>
            </div>
          ) : (
            // Step 2: Preview results
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-900">{importPreview.total_rows}</div>
                  <div className="text-xs text-gray-500">Total Rows</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-600">{importPreview.will_create}</div>
                  <div className="text-xs text-green-600">Will Create</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-blue-600">{importPreview.will_update}</div>
                  <div className="text-xs text-blue-600">Will Update</div>
                </div>
                {importPreview.will_skip > 0 && (
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-gray-500">{importPreview.will_skip}</div>
                    <div className="text-xs text-gray-500">Will Skip</div>
                  </div>
                )}
                {importPreview.will_delete > 0 && (
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-red-600">{importPreview.will_delete}</div>
                    <div className="text-xs text-red-600">Will Delete</div>
                  </div>
                )}
                <div className={`rounded-lg p-3 text-center ${importPreview.error_count > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <div className={`text-xl font-bold ${importPreview.error_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {importPreview.error_count}
                  </div>
                  <div className={`text-xs ${importPreview.error_count > 0 ? 'text-red-600' : 'text-gray-500'}`}>Errors</div>
                </div>
              </div>

              {/* Tab navigation for preview */}
              <div className="flex gap-2 border-b border-border">
                <button
                  onClick={() => setPreviewTab('summary')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    previewTab === 'summary' 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setPreviewTab('all')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    previewTab === 'all' 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  All Rows ({importPreview.total_rows})
                </button>
                {importPreview.error_count > 0 && (
                  <button
                    onClick={() => setPreviewTab('errors')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                      previewTab === 'errors' 
                        ? 'border-red-600 text-red-600' 
                        : 'border-transparent text-red-500 hover:text-red-700'
                    }`}
                  >
                    Errors ({importPreview.error_count})
                  </button>
                )}
                {importPreview.will_delete > 0 && (
                  <button
                    onClick={() => setPreviewTab('delete')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                      previewTab === 'delete' 
                        ? 'border-red-600 text-red-600' 
                        : 'border-transparent text-red-500 hover:text-red-700'
                    }`}
                  >
                    To Delete ({importPreview.will_delete})
                  </button>
                )}
              </div>

              {/* Preview content */}
              {previewTab === 'summary' ? (
                <div className="space-y-4">
                  {/* Items to be created/updated grouped by SKU */}
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Category</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600">Order</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600">Tiers</th>
                          <th className="px-3 py-2 text-center font-medium text-gray-600">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {Object.entries(importPreview.parsed_items || {}).map(([sku, item]) => (
                          <tr key={sku} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-gray-600">{sku}</td>
                            <td className="px-3 py-2 text-gray-900">{item.display_name}</td>
                            <td className="px-3 py-2 text-gray-600">{item.category || '—'}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{item.show_order || 0}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{item.tiers?.length || 0}</td>
                            <td className="px-3 py-2 text-center">{getActionBadge(item.action)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : previewTab === 'delete' ? (
                // Delete tab view
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-red-700">SKU</th>
                        <th className="px-3 py-2 text-left font-medium text-red-700">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(importPreview.items_to_delete || []).map((sku, idx) => (
                        <tr key={idx} className="bg-red-50/50 hover:bg-red-50">
                          <td className="px-3 py-2 font-mono text-gray-700">{sku}</td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Will Delete</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // All rows or errors view
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Row</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">Tiers</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">Action</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredRows.map((row, idx) => (
                        <tr key={idx} className={row.action === 'error' ? 'bg-red-50' : row.action === 'skip' ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2 text-gray-500">{row.row_num}</td>
                          <td className="px-3 py-2 font-mono text-gray-600">{row.sales_sku || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.display_name || '—'}</td>
                          <td className="px-3 py-2 text-center text-gray-600">{row.tiers_count || 0}</td>
                          <td className="px-3 py-2 text-center">{getActionBadge(row.action)}</td>
                          <td className="px-3 py-2">
                            {row.errors?.length > 0 && (
                              <ul className="text-xs text-red-600">
                                {row.errors.map((err, i) => (
                                  <li key={i}>• {err}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {importPreview.error_count > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Some rows have errors</p>
                    <p className="text-sm text-amber-700">
                      {importPreview.error_count} rows will be skipped. Fix the errors in your file and re-upload to include them.
                    </p>
                  </div>
                </div>
              )}

              {importPreview.will_delete > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Items will be deleted</p>
                    <p className="text-sm text-red-700">
                      {importPreview.will_delete} items that are not in the import file will be permanently removed from the rate card.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-gray-50">
          <button
            onClick={importPreview ? onReset : onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {importPreview ? 'Back' : 'Cancel'}
          </button>
          <div className="flex items-center gap-3">
            {!importPreview ? (
              <button
                onClick={onPreview}
                disabled={!importFile || importLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Preview Import
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onCommit}
                disabled={importCommitting || (importPreview.valid_rows === 0 && importPreview.will_delete === 0)}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  importPreview.will_delete > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {importCommitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {(() => {
                      const parts = [];
                      if (importPreview.will_create > 0) parts.push(`${importPreview.will_create} create`);
                      if (importPreview.will_update > 0) parts.push(`${importPreview.will_update} update`);
                      if (importPreview.will_delete > 0) parts.push(`${importPreview.will_delete} delete`);
                      return parts.length > 0 ? `Confirm (${parts.join(', ')})` : 'Confirm Import';
                    })()}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



// ==================== Artwork Templates Picker =====================
// Multi-select for linking artwork templates to a Sales Rate Item.
function ArtworkTemplatesPicker({ value, onChange }) {
  const [available, setAvailable] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/artwork-templates/admin?include_archived=false`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      setAvailable(data || []);
    }).catch(() => setAvailable([]))
      .finally(() => setLoading(false));
  }, []);

  const selectedIds = new Set(value || []);
  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };

  const filtered = available.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (t.name || '').toLowerCase().includes(s) ||
      (t.category || '').toLowerCase().includes(s) ||
      (t.subcategory || '').toLowerCase().includes(s) ||
      (t.product_type || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="border border-indigo-200 bg-indigo-50/40 rounded-lg p-4 space-y-3" data-testid="sku-artwork-templates-picker">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-indigo-900">
          Linked artwork templates ({(value || []).length} selected)
        </label>
        <a
          href="/admin/artwork-templates"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-indigo-700 hover:underline"
        >
          Manage library →
        </a>
      </div>
      <p className="text-xs text-indigo-800">
        Templates linked here auto-appear on the customer order page under &quot;Templates for this order&quot;.
      </p>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search templates by name, category, product…"
        className="w-full border border-indigo-200 rounded px-2 py-1.5 text-sm bg-white"
        data-testid="sku-template-search"
      />
      {loading ? (
        <p className="text-xs text-slate-500">Loading templates…</p>
      ) : available.length === 0 ? (
        <p className="text-xs text-slate-500">
          No templates yet.{' '}
          <a href="/admin/artwork-templates" target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">Upload some first</a>.
        </p>
      ) : (
        <div className="max-h-56 overflow-y-auto bg-white rounded border border-indigo-100 divide-y">
          {filtered.map(tpl => (
            <label
              key={tpl.id}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer"
              data-testid={`sku-template-option-${tpl.id}`}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(tpl.id)}
                onChange={() => toggle(tpl.id)}
                className="h-4 w-4"
              />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{tpl.name}</div>
                <div className="text-xs text-slate-500">
                  {(tpl.product_type || '').toUpperCase()}
                  {tpl.category && ` · ${tpl.category}`}
                  {tpl.subcategory && ` · ${tpl.subcategory}`}
                  {` · v${tpl.version || 1}`}
                </div>
              </div>
            </label>
          ))}
          {filtered.length === 0 && (
            <p className="text-xs text-slate-500 px-3 py-4 text-center">No templates match your search.</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * LinkedTemplatesDialog — standalone modal for quick inspection / editing
 * of the Artwork Templates linked to a single Sales Rate Item, opened from
 * the "Templates" count badge on the pricing catalogue list.
 *
 * Deliberately reuses `ArtworkTemplatesPicker` so the picker behaviour
 * (search, checkbox list, "Manage library" link) stays identical to the
 * full item-edit modal — the only difference is that this dialog saves
 * ONLY the `artwork_template_ids` field via the partial-update endpoint
 * so admins can adjust template links without opening the full form.
 */
function LinkedTemplatesDialog({ item, onClose, onSaved }) {
  const [ids, setIds] = React.useState(() => item.artwork_template_ids || []);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/sales/items/${item.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ artwork_template_ids: ids }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Save failed (${res.status})`);
      }
      onSaved?.(ids);
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="linked-templates-dialog-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="linked-templates-dialog"
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              Linked Artwork Templates
            </h3>
            <p className="text-sm text-gray-500 mt-0.5 truncate">
              <span className="font-mono text-xs">{item.sales_sku}</span>
              {item.display_name ? <> · {item.display_name}</> : null}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1 rounded hover:bg-gray-100 text-gray-500"
            data-testid="linked-templates-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <ArtworkTemplatesPicker value={ids} onChange={setIds} />
          {error && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
            data-testid="linked-templates-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="linked-templates-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
